var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as ebml from 'ts-ebml/dist/EBML.min.js';
import { v4 as uuidV4 } from 'uuid';
import { getRoomName } from '../../../base/conference/functions';
import { isMobileBrowser } from '../../../base/environment/utils';
import { browser } from '../../../base/lib-meet-hour';
import { MEDIA_TYPE } from '../../../base/media/constants';
import { StateListenerRegistry } from '../../../base/redux';
import { getLocalTrack, getTrackState } from '../../../base/tracks/functions';
import { isEmbedded } from '../../../base/util/embedUtils';
import { downloadBlob } from '../../../local-recording/recording/Utils';
import { isParentRecordingDownloadBridgeAvailable, requestParentRecordingDownload } from './downloadBridge.web';
import { stopLocalVideoRecording } from '../../actions.any';
import logger from '../../logger';
/**
 * After a lot of trial and error, this is the preferred media type for
 * local recording. It is the only one that works across all platforms, with the
 * only caveat being that the resulting file wouldn't be seekable.
 *
 * We solve that by fixing the first Blob in order to reserve the space for the
 * corrected metadata, and after the recording is done, we do it again, this time with
 * the real duration, and overwrite the first part of the file.
 */
const PREFERRED_MEDIA_TYPE = 'video/webm;codecs=vp8,opus';
const VIDEO_BIT_RATE = 2500000; // 2.5Mbps in bits
const LocalRecordingManager = {
    recorder: undefined,
    stream: undefined,
    audioContext: undefined,
    audioDestination: undefined,
    roomName: '',
    selfRecording: {
        on: false,
        withVideo: false
    },
    selfRecordingSources: undefined,
    stopRequested: false,
    firstChunk: undefined,
    fileHandle: undefined,
    fallbackChunks: [],
    shouldWarnAboutMissingParentDownloadBridge: false,
    startTime: undefined,
    writableStream: undefined,
    get mediaType() {
        if (this.selfRecording.on && !this.selfRecording.withVideo) {
            return 'audio/webm;';
        }
        return PREFERRED_MEDIA_TYPE;
    },
    /**
     * Initializes audio context used for mixing audio tracks.
     *
     * @returns {void}
     */
    initializeAudioMixer() {
        this.audioContext = new AudioContext();
        this.audioDestination = this.audioContext.createMediaStreamDestination();
    },
    /**
     * Mixes multiple audio tracks to the destination media stream.
     *
     * @param {MediaStream} stream - The stream to mix.
     * @returns {void}
     * */
    mixAudioStream(stream) {
        var _a;
        if (stream.getAudioTracks().length > 0 && this.audioDestination) {
            (_a = this.audioContext) === null || _a === void 0 ? void 0 : _a.createMediaStreamSource(stream).connect(this.audioDestination);
        }
    },
    /**
     * Adds audio track to the recording stream.
     *
     * @param {MediaStreamTrack} track - The track to be added.
     * @returns {void}
     */
    addAudioTrackToLocalRecording(track) {
        if (this.selfRecording.on) {
            return;
        }
        if (track) {
            const stream = new MediaStream([track]);
            this.mixAudioStream(stream);
        }
    },
    /**
     * Returns a filename based ono the Jitsi room name in the URL and timestamp.
     *
     * @returns {string}
     * */
    getFilename() {
        const now = new Date();
        const timestamp = now.toISOString();
        return `${this.roomName}_${timestamp}`;
    },
    /**
     * Stops local recording.
     *
     * @returns {void}
     * */
    stopLocalRecording() {
        var _a;
        this.stopRequested = true;
        (_a = this.recorder) === null || _a === void 0 ? void 0 : _a.stop();
    },
    /**
     * Points a running self recording at the conference's current microphone and camera tracks. They are replaced
     * whenever the camera is turned off and on (on web that stops the camera and opens a new one), when virtual
     * background or noise suppression is toggled, and when a device is switched or recovered.
     *
     * @param {Object[]} tracks - The redux tracks state.
     * @returns {void}
     */
    updateSelfRecordingSources(tracks) {
        var _a, _b, _c, _d;
        if (!this.selfRecordingSources) {
            return;
        }
        _connectSelfAudio(this, _liveTrack((_b = (_a = getLocalTrack(tracks, MEDIA_TYPE.AUDIO)) === null || _a === void 0 ? void 0 : _a.mhTrack) === null || _b === void 0 ? void 0 : _b.track));
        _pipeSelfVideo(this, _liveTrack((_d = (_c = getLocalTrack(tracks, MEDIA_TYPE.VIDEO)) === null || _c === void 0 ? void 0 : _c.mhTrack) === null || _d === void 0 ? void 0 : _d.track));
    },
    /**
     * Starts a local recording.
     *
     * @param {IStore} store - The redux store.
     * @param {boolean} onlySelf - Whether to record only self streams.
     * @returns {void}
     */
    startLocalRecording(store, onlySelf) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
            const { dispatch, getState } = store;
            this.roomName = (_a = getRoomName(getState())) !== null && _a !== void 0 ? _a : '';
            this.firstChunk = undefined;
            this.fileHandle = undefined;
            this.writableStream = undefined;
            this.fallbackChunks = [];
            this.shouldWarnAboutMissingParentDownloadBridge = false;
            this.stopRequested = false;
            // Get a handle to the file we are going to write.
            const options = {
                startIn: 'downloads',
                suggestedName: `${this.getFilename()}.webm`
            };
            try {
                // Chromium builds that ship without the File System Access API (Brave by default) still pass
                // isSupported() through the download fallback, so the picker cannot be assumed to exist.
                if (typeof window.showSaveFilePicker === 'function') {
                    this.fileHandle = yield window.showSaveFilePicker(options);
                    this.writableStream = yield ((_b = this.fileHandle) === null || _b === void 0 ? void 0 : _b.createWritable());
                }
                else if (isEmbedded()) {
                    // Same download fallback as a picker refused inside an iframe, so the same bridge warning applies.
                    this.shouldWarnAboutMissingParentDownloadBridge = !(yield isParentRecordingDownloadBridgeAvailable());
                }
            }
            catch (error) {
                // AbortError occurs when the user cancels the file picker dialog - this is normal and benign
                if ((error === null || error === void 0 ? void 0 : error.name) === 'AbortError') {
                    logger.debug('File picker cancelled by user. Falling back to blob download.');
                    return;
                }
                const isCrossOriginSubFrameFilePickerError = (error === null || error === void 0 ? void 0 : error.name) === 'SecurityError' && window.top !== window.self;
                // The platform refused write access to the picked file. The desktop app does this: Electron hands back a
                // file handle from showSaveFilePicker but does not grant the File System Access write permission, so
                // createWritable() rejects. The recording itself is unaffected - it is saved through the same in-memory
                // download used when the picker is blocked in an iframe, instead of failing before it starts.
                const isWriteAccessRefused = (error === null || error === void 0 ? void 0 : error.name) === 'NotAllowedError';
                if (!isCrossOriginSubFrameFilePickerError && !isWriteAccessRefused) {
                    throw error;
                }
                this.fileHandle = undefined;
                this.writableStream = undefined;
                logger.warn('Writing to a picked file is not available here. Falling back to blob download.', error);
                if (isEmbedded()) {
                    const parentBridgeAvailable = yield isParentRecordingDownloadBridgeAvailable();
                    this.shouldWarnAboutMissingParentDownloadBridge = !parentBridgeAvailable;
                }
            }
            const supportsCaptureHandle = !isEmbedded() && Boolean(navigator.mediaDevices.setCaptureHandleConfig);
            const tabId = uuidV4();
            this.selfRecording.on = onlySelf;
            let gdmStream = new MediaStream();
            const tracks = getTrackState(getState());
            if (onlySelf) {
                const audioTrack = _liveTrack((_d = (_c = getLocalTrack(tracks, MEDIA_TYPE.AUDIO)) === null || _c === void 0 ? void 0 : _c.mhTrack) === null || _d === void 0 ? void 0 : _d.track);
                const videoTrack = _liveTrack((_f = (_e = getLocalTrack(tracks, MEDIA_TYPE.VIDEO)) === null || _e === void 0 ? void 0 : _e.mhTrack) === null || _f === void 0 ? void 0 : _f.track);
                if (!audioTrack && !videoTrack) {
                    throw new Error('NoLocalStreams');
                }
                this.selfRecording.withVideo = Boolean(videoTrack);
                this.stream = _createSelfRecordingStream(this, audioTrack, videoTrack);
            }
            else {
                if (supportsCaptureHandle) {
                    navigator.mediaDevices.setCaptureHandleConfig({
                        handle: `MeetHour-${tabId}`,
                        permittedOrigins: ['*']
                    });
                }
                gdmStream = yield navigator.mediaDevices.getDisplayMedia({
                    video: {
                        displaySurface: 'browser',
                        frameRate: 30
                    },
                    audio: {
                        autoGainControl: false,
                        channelCount: 2,
                        echoCancellation: false,
                        noiseSuppression: false,
                        restrictOwnAudio: false,
                        suppressLocalAudioPlayback: false
                    },
                    preferCurrentTab: true,
                    surfaceSwitching: 'exclude'
                });
                const gdmVideoTrack = gdmStream.getVideoTracks()[0];
                const isBrowser = gdmVideoTrack.getSettings().displaySurface === 'browser';
                const matchesHandle = supportsCaptureHandle
                    && ((_g = gdmVideoTrack.getCaptureHandle()) === null || _g === void 0 ? void 0 : _g.handle) === `MeetHour-${tabId}`;
                if (!isBrowser || (supportsCaptureHandle && !matchesHandle)) {
                    gdmStream.getTracks().forEach((track) => track.stop());
                    throw new Error('WrongSurfaceSelected');
                }
                this.initializeAudioMixer();
                const gdmAudioTrack = gdmStream.getAudioTracks()[0];
                if (!gdmAudioTrack) {
                    throw new Error('NoAudioTrackFound');
                }
                this.addAudioTrackToLocalRecording(gdmAudioTrack);
                const localAudioTrack = (_j = (_h = getLocalTrack(tracks, MEDIA_TYPE.AUDIO)) === null || _h === void 0 ? void 0 : _h.mhTrack) === null || _j === void 0 ? void 0 : _j.track;
                if (localAudioTrack) {
                    this.addAudioTrackToLocalRecording(localAudioTrack);
                }
                this.stream = new MediaStream([
                    ...((_k = this.audioDestination) === null || _k === void 0 ? void 0 : _k.stream.getAudioTracks()) || [],
                    gdmVideoTrack
                ]);
            }
            try {
                this.recorder = new MediaRecorder(this.stream, {
                    audioBitrateMode: 'constant',
                    mimeType: this.mediaType,
                    videoBitsPerSecond: VIDEO_BIT_RATE
                });
            }
            catch (error) {
                // Nothing will stop the self recording inputs (they read the live camera) if the recorder never starts.
                _releaseSelfRecordingSources(this);
                (_l = this.audioContext) === null || _l === void 0 ? void 0 : _l.close().catch(() => undefined);
                this.audioContext = undefined;
                this.audioDestination = undefined;
                throw error;
            }
            this.recorder.addEventListener('dataavailable', (e) => __awaiter(this, void 0, void 0, function* () {
                if (this.recorder && e.data && e.data.size > 0) {
                    let data = e.data;
                    if (!this.firstChunk) {
                        this.firstChunk = data;
                        if (this.writableStream) {
                            data = yield fixDuration(data, 864000000); // Reserve 24h.
                        }
                    }
                    if (this.writableStream) {
                        yield this.writableStream.write(data);
                    }
                    else {
                        this.fallbackChunks.push(data);
                    }
                }
            }));
            this.recorder.addEventListener('start', () => {
                this.startTime = Date.now();
            });
            this.recorder.addEventListener('stop', () => __awaiter(this, void 0, void 0, function* () {
                var _a, _b;
                const duration = Date.now() - this.startTime;
                // The browser stops a recorder on its own when it fails or when every recorded track has ended. Nobody
                // asked for this stop, so tell the app, or it keeps showing a recording that Stop can no longer end.
                if (!this.stopRequested) {
                    dispatch(stopLocalVideoRecording());
                }
                if (this.selfRecording.on) {
                    // Stops only the tracks the manager created; the conference's own camera and microphone are inputs,
                    // never part of the recorded stream - unless the camera had to be recorded directly (see
                    // _createSelfRecordingStream), and that one must keep running.
                    _releaseSelfRecordingSources(this);
                }
                else {
                    (_a = this.stream) === null || _a === void 0 ? void 0 : _a.getTracks().forEach((track) => track.stop());
                }
                gdmStream === null || gdmStream === void 0 ? void 0 : gdmStream.getTracks().forEach((track) => track.stop());
                // The stop event is emitted when the recorder is done, and _after_ the last buffered
                // data has been handed over to the dataavailable event.
                this.recorder = undefined;
                // Close it, do not just drop the reference: a running AudioContext is owned by the browser's audio
                // thread and survives without one, still rendering and still holding one of the ~6 contexts a document
                // may have. A local recording is started and stopped repeatedly over a long meeting.
                (_b = this.audioContext) === null || _b === void 0 ? void 0 : _b.close().catch((error) => logger.error('Failed to close the local recording AudioContext', error));
                this.audioContext = undefined;
                this.audioDestination = undefined;
                this.startTime = undefined;
                if (this.writableStream) {
                    try {
                        if (this.firstChunk) {
                            yield this.writableStream.seek(0);
                            yield this.writableStream.write(yield fixDuration(this.firstChunk, duration));
                        }
                        yield this.writableStream.close();
                    }
                    catch (e) {
                        logger.error('Error while writing to the local recording file', e);
                    }
                    finally {
                        this.firstChunk = undefined;
                        this.fileHandle = undefined;
                        this.writableStream = undefined;
                        this.fallbackChunks = [];
                    }
                }
                else if (this.fallbackChunks.length) {
                    try {
                        const outputChunks = [...this.fallbackChunks];
                        if (this.firstChunk) {
                            outputChunks[0] = yield fixDuration(this.firstChunk, duration);
                        }
                        const outputFileName = `${this.getFilename()}.webm`;
                        const outputBlob = new Blob(outputChunks, { type: this.mediaType });
                        const downloadedByParent = yield requestParentRecordingDownload(outputBlob, outputFileName);
                        if (!downloadedByParent) {
                            downloadBlob(outputBlob, outputFileName);
                        }
                    }
                    catch (e) {
                        logger.error('Error while preparing local recording download', e);
                    }
                    finally {
                        this.firstChunk = undefined;
                        this.fileHandle = undefined;
                        this.writableStream = undefined;
                        this.fallbackChunks = [];
                    }
                }
            }));
            if (!onlySelf) {
                gdmStream === null || gdmStream === void 0 ? void 0 : gdmStream.addEventListener('inactive', () => {
                    dispatch(stopLocalVideoRecording());
                });
                this.stream.addEventListener('inactive', () => {
                    dispatch(stopLocalVideoRecording());
                });
            }
            this.recorder.start(5000);
        });
    },
    /**
     * Whether or not local recording is supported.
     *
     * @returns {boolean}
     */
    isSupported() {
        var _a;
        const hasCaptureHandleSupport = Boolean(navigator.mediaDevices.setCaptureHandleConfig);
        const hasSaveFilePicker = typeof window.showSaveFilePicker !== 'undefined';
        const hasDownloadFallback = typeof ((_a = window.URL) === null || _a === void 0 ? void 0 : _a.createObjectURL) === 'function';
        return browser.isChromiumBased()
            && !browser.isElectron()
            && !browser.isReactNative()
            && !isMobileBrowser()
            && (isEmbedded() || hasCaptureHandleSupport)
            && (hasSaveFilePicker || hasDownloadFallback)
            && MediaRecorder.isTypeSupported(PREFERRED_MEDIA_TYPE);
    },
    /**
     * Whether or not we're currently recording locally.
     *
     * @returns {boolean}
     */
    isRecordingLocally() {
        return Boolean(this.recorder);
    }
};
/**
 * Returns the track if it is live.
 *
 * @param {MediaStreamTrack} [track] - The track.
 * @returns {MediaStreamTrack|undefined}
 */
function _liveTrack(track) {
    return (track === null || track === void 0 ? void 0 : track.readyState) === 'live' ? track : undefined;
}
/**
 * Creates the stream a self recording records. Its tracks belong to the manager and live for the whole recording,
 * so the recorder never sees the conference's camera or microphone track end: the microphone is fed through the
 * audio mixer and the camera's frames are copied into a generated video track, and both inputs are swapped by
 * {@code updateSelfRecordingSources} when the conference replaces them.
 *
 * @param {Object} manager - The local recording manager.
 * @param {MediaStreamTrack} [audioTrack] - The current microphone track.
 * @param {MediaStreamTrack} [videoTrack] - The current camera track.
 * @returns {MediaStream}
 */
function _createSelfRecordingStream(manager, audioTrack, videoTrack) {
    var _a, _b;
    manager.initializeAudioMixer();
    manager.selfRecordingSources = {};
    if (((_a = manager.audioContext) === null || _a === void 0 ? void 0 : _a.state) === 'suspended') {
        manager.audioContext.resume().catch((error) => {
            logger.error('Failed to resume the local recording AudioContext', error);
        });
    }
    // Recorded even when the microphone is missing at the start, so a microphone added later is recorded.
    const tracks = [...((_b = manager.audioDestination) === null || _b === void 0 ? void 0 : _b.stream.getAudioTracks()) || []];
    _connectSelfAudio(manager, audioTrack);
    if (videoTrack) {
        const Generator = window.MediaStreamTrackGenerator;
        const Processor = window.MediaStreamTrackProcessor;
        if (typeof Generator === 'function' && typeof Processor === 'function') {
            const videoGenerator = new Generator({ kind: 'video' });
            manager.selfRecordingSources.videoGenerator = videoGenerator;
            manager.selfRecordingSources.videoWriter = videoGenerator.writable.getWriter();
            _pipeSelfVideo(manager, videoTrack);
            tracks.push(videoGenerator);
        }
        else {
            // Without these APIs the camera can only be recorded directly, as before: a camera change ends the
            // recorded video.
            tracks.push(videoTrack);
        }
    }
    return new MediaStream(tracks);
}
/**
 * Feeds a microphone track into the self recording, replacing the previous one.
 *
 * @param {Object} manager - The local recording manager.
 * @param {MediaStreamTrack} [track] - The microphone track; none records silence.
 * @returns {void}
 */
function _connectSelfAudio(manager, track) {
    var _a;
    const sources = manager.selfRecordingSources;
    if (!sources || sources.audioTrack === track) {
        return;
    }
    (_a = sources.audioSource) === null || _a === void 0 ? void 0 : _a.disconnect();
    sources.audioSource = undefined;
    sources.audioTrack = track;
    if (track && manager.audioContext && manager.audioDestination) {
        sources.audioSource = manager.audioContext.createMediaStreamSource(new MediaStream([track]));
        sources.audioSource.connect(manager.audioDestination);
    }
}
/**
 * Interval of the black frames written while the camera is off.
 */
const CAMERA_OFF_FRAME_INTERVAL = 500;
/**
 * Copies the frames of a camera track into the self recording's video track, replacing the previous camera track.
 * While there is no camera track, black frames are written instead: Chrome's WebM muxer holds back the audio while
 * the video track produces no frames, so without them a camera-off stretch would sit in memory until the camera
 * returned.
 *
 * @param {Object} manager - The local recording manager.
 * @param {MediaStreamTrack} [track] - The camera track.
 * @returns {void}
 */
function _pipeSelfVideo(manager, track) {
    var _a;
    const sources = manager.selfRecordingSources;
    if (!(sources === null || sources === void 0 ? void 0 : sources.videoWriter) || sources.videoTrack === track) {
        return;
    }
    (_a = sources.videoPump) === null || _a === void 0 ? void 0 : _a.cancel();
    sources.videoPump = undefined;
    window.clearInterval(sources.videoFiller);
    sources.videoFiller = undefined;
    sources.videoTrack = track;
    if (!track) {
        _fillSelfVideo(sources);
        return;
    }
    const reader = new window.MediaStreamTrackProcessor({ track }).readable.getReader();
    const writer = sources.videoWriter;
    const pump = {
        cancelled: false,
        cancel: () => {
            pump.cancelled = true;
            reader.cancel().catch(() => undefined);
        }
    };
    sources.videoPump = pump;
    (() => __awaiter(this, void 0, void 0, function* () {
        try {
            while (!pump.cancelled) {
                const { done, value } = yield reader.read();
                if (done) {
                    break;
                }
                if (pump.cancelled) {
                    value.close();
                    break;
                }
                sources.lastFrame = {
                    height: value.displayHeight,
                    timestamp: value.timestamp,
                    wallTime: performance.now(),
                    width: value.displayWidth
                };
                yield writer.write(value);
            }
        }
        catch (error) {
            if (!pump.cancelled) {
                logger.error('Local recording stopped receiving camera frames', error);
            }
        }
    }))();
}
/**
 * Writes black frames into the self recording's video track until a camera track is piped in again. Their
 * timestamps continue from the last camera frame, so the recorded timeline stays on the cameras' clock.
 *
 * @param {Object} sources - The self recording inputs.
 * @returns {void}
 */
function _fillSelfVideo(sources) {
    const { lastFrame, videoWriter } = sources;
    const VideoFrameClass = window.VideoFrame;
    if (!lastFrame || !videoWriter || typeof VideoFrameClass !== 'function') {
        return;
    }
    const canvas = new OffscreenCanvas(lastFrame.width, lastFrame.height);
    const context = canvas.getContext('2d');
    if (!context) {
        return;
    }
    context.fillStyle = '#000';
    context.fillRect(0, 0, lastFrame.width, lastFrame.height);
    sources.videoFiller = window.setInterval(() => {
        const timestamp = lastFrame.timestamp + Math.round((performance.now() - lastFrame.wallTime) * 1000);
        videoWriter.write(new VideoFrameClass(canvas, { timestamp })).catch(() => undefined);
    }, CAMERA_OFF_FRAME_INTERVAL);
}
/**
 * Stops the self recording inputs and the tracks the manager created for them.
 *
 * @param {Object} manager - The local recording manager.
 * @returns {void}
 */
function _releaseSelfRecordingSources(manager) {
    var _a, _b, _c, _d;
    const sources = manager.selfRecordingSources;
    if (!sources) {
        return;
    }
    manager.selfRecordingSources = undefined;
    window.clearInterval(sources.videoFiller);
    (_a = sources.videoPump) === null || _a === void 0 ? void 0 : _a.cancel();
    (_b = sources.audioSource) === null || _b === void 0 ? void 0 : _b.disconnect();
    (_c = sources.videoWriter) === null || _c === void 0 ? void 0 : _c.close().catch(() => undefined);
    (_d = sources.videoGenerator) === null || _d === void 0 ? void 0 : _d.stop();
}
StateListenerRegistry.register(
/* selector */ state => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    if (!LocalRecordingManager.selfRecordingSources) {
        return '';
    }
    const tracks = getTrackState(state);
    return `${(_d = (_c = (_b = (_a = getLocalTrack(tracks, MEDIA_TYPE.AUDIO)) === null || _a === void 0 ? void 0 : _a.mhTrack) === null || _b === void 0 ? void 0 : _b.track) === null || _c === void 0 ? void 0 : _c.id) !== null && _d !== void 0 ? _d : ''}|${(_h = (_g = (_f = (_e = getLocalTrack(tracks, MEDIA_TYPE.VIDEO)) === null || _e === void 0 ? void 0 : _e.mhTrack) === null || _f === void 0 ? void 0 : _f.track) === null || _g === void 0 ? void 0 : _g.id) !== null && _h !== void 0 ? _h : ''}`;
}, 
/* listener */ (key, { getState }) => {
    key && LocalRecordingManager.updateSelfRecordingSources(getTrackState(getState()));
});
/**
 * Fixes the duration in the WebM container metadata.
 * Note: cues are omitted.
 *
 * @param {Blob} data - The first Blob of WebM data.
 * @param {number} duration - Actual duration of the video in milliseconds.
 * @returns {Promise<Blob>}
 */
function fixDuration(data, duration) {
    return __awaiter(this, void 0, void 0, function* () {
        const decoder = new ebml.Decoder();
        const reader = new ebml.Reader();
        reader.logging = false;
        reader.drop_default_duration = false;
        const dataBuf = yield data.arrayBuffer();
        const elms = decoder.decode(dataBuf);
        for (const elm of elms) {
            reader.read(elm);
        }
        reader.stop();
        const newMetadataBuf = ebml.tools.makeMetadataSeekable(reader.metadatas, duration, [] // No cues
        );
        const body = new Uint8Array(dataBuf).subarray(reader.metadataSize);
        return new Blob([newMetadataBuf, body], { type: data.type });
    });
}
export default LocalRecordingManager;
